import PropTypes from 'prop-types';
import Immutable from 'seamless-immutable';
import { push } from 'connected-react-router';
import { objectIsEmpty } from '@widergy/web-utils/lib/object';
import { retrieveErrors, retrieveSuccessMessage } from '@widergy/web-utils/lib/message';
import { uploadFiles } from '@widergy/web-utils/lib/form';
import Polling from '@widergy/polling-handling';
import { mapUserFormValues } from '@widergy/utilitygo-forms-web';
import { AlertHandler } from '@widergy/energy-ui';

import FormsService from 'services/FormsService';
import { CLAIM, PROCEDURE } from 'constants/userForms';
import { message } from 'utils/stringUtils';
import { ROUTES } from 'constants/routes';
import { appConfig } from 'config/customization';
import { handleSubmitFormFailure, handleUploadFailure } from 'utils/formUtils';

import SurveyActions from './survey/actions';
import { actionCreators as alertActions } from './alertHandlers';
import { actionCreators as accountActions } from './accountHandlers';
import { stringArrayToObject } from './utils';

/* ------------- Form actions ------------- */
export const actions = stringArrayToObject(
  [
    'GET_FORM_RESPONSES',
    'GET_FORM_RESPONSES_SUCCESS',
    'GET_FORM_RESPONSES_FAILURE',
    'GET_FORM_CATEGORIES',
    'GET_FORM_CATEGORIES_SUCCESS',
    'GET_FORM_CATEGORIES_FAILURE',
    'GET_FORM',
    'GET_FORM_SUCCESS',
    'GET_FORM_FAILURE',
    'CLEAR_FORM',
    'CLEAR_FORM_CATEGORIES',
    'SUBMIT_FORM',
    'SUBMIT_FORM_SUCCESS',
    'SUBMIT_FORM_FAILURE',
    'REITERATION',
    'REITERATION_SUCCESS',
    'REITERATION_FAILURE',
    'CANCELATION',
    'CANCELATION_SUCCESS',
    'CANCELATION_FAILURE',
    'APPEND_FORM_RESPONSES',
    'APPEND_FORM_RESPONSES_SUCCESS',
    'APPEND_FORM_RESPONSES_FAILURE',
    'CREATE_FORM',
    'GET_FORM_RESPONSE_DETAILS',
    'GET_FORM_RESPONSE_DETAILS_SUCCESS',
    'GET_FORM_RESPONSE_DETAILS_FAILURE',
    'RESET_FORM_RESPONSE_DETAILS'
  ],
  '@@FORM-RESPONSE'
);

const privateActionCreators = {
  getFormResponsesSuccess: (formResponses, type) => ({
    type: actions.GET_FORM_RESPONSES_SUCCESS,
    payload: formResponses,
    target: `${type}FormResponses`
  }),
  getFormCategoriesSuccess: (formCategories, type) => ({
    type: actions.GET_FORM_CATEGORIES_SUCCESS,
    payload: formCategories,
    target: `${type}FormCategories`
  }),
  getFormSuccess: form => ({
    type: actions.GET_FORM_SUCCESS,
    payload: form,
    target: 'form'
  }),
  reiterationSuccess: (formResponses, type, formName) => ({
    type: actions.REITERATION_SUCCESS,
    payload: formResponses,
    formType: type,
    formName,
    target: `${type}FormResponses`
  }),
  cancelationSuccess: (formResponses, type, formName) => ({
    type: actions.CANCELATION_SUCCESS,
    payload: formResponses,
    formType: type,
    formName,
    target: `${type}FormResponses`
  }),
  appendFormResponsesSuccess: (formResponses, type) => ({
    type: actions.APPEND_FORM_RESPONSES_SUCCESS,
    payload: formResponses,
    target: `${type}FormResponses`
  }),
  getFormResponsesFailure: (error, type) => ({
    type: actions.GET_FORM_RESPONSES_FAILURE,
    payload: { error },
    target: `${type}FormResponses`
  }),
  getFormCategoriesFailure: (error, type) => ({
    type: actions.GET_FORM_CATEGORIES_FAILURE,
    payload: { error },
    target: `${type}FormCategories`
  }),
  getFormFailure: error => ({
    type: actions.GET_FORM_FAILURE,
    payload: { error },
    target: 'form'
  }),
  submitFormFailure: (error, formType, formName, isPublic = false) => ({
    type: actions.SUBMIT_FORM_FAILURE,
    payload: { error, formType, formName, isPublic },
    target: 'submitForm'
  }),
  submitFormSuccess: (formType, formName, isPublic = false, complaint) => ({
    type: actions.SUBMIT_FORM_SUCCESS,
    payload: { formType, formName, isPublic, complaint },
    target: 'submitForm'
  }),
  reiterationFailure: (error, type) => ({
    type: actions.REITERATION_FAILURE,
    payload: { error },
    target: `${type}FormResponses`
  }),
  cancelationFailure: (error, type) => ({
    type: actions.CANCELATION_FAILURE,
    payload: { error },
    target: `${type}FormResponses`
  }),
  appendFormResponsesFailure: error => ({
    type: actions.APPEND_FORM_RESPONSES_FAILURE,
    payload: { error },
    target: 'formResponses'
  }),
  getFormResponseDetailsSuccess: (formDetails, formType) => ({
    type: actions.GET_FORM_RESPONSE_DETAILS_SUCCESS,
    payload: formDetails,
    formType,
    target: 'formDetails'
  }),
  getFormResponseDetailsFailure: error => ({
    type: actions.GET_FORM_RESPONSE_DETAILS_FAILURE,
    payload: error,
    target: 'formDetails'
  })
};

export const actionCreators = {
  getFormResponses({ type, pageSize }) {
    return async (dispatch, getState) => {
      const { currentAccount } = getState().account;
      if (!currentAccount) return;
      const accountId = currentAccount.id;
      dispatch({ type: actions.GET_FORM_RESPONSES, target: `${type}FormResponses` });
      const response = await Polling.handle(
        FormsService.getFormResponses(accountId, { type, pageNumber: 1, pageSize })
      );
      if (response.error) {
        AlertHandler.error(
          retrieveErrors(response.error, message('DefaultMessages:getFormResponsesFailure'))
        );
        dispatch(privateActionCreators.getFormResponsesFailure(response.error, type));
      } else {
        dispatch(privateActionCreators.getFormResponsesSuccess(response.data, type));
        dispatch(accountActions.claimsUpdated());
      }
    };
  },
  appendFormResponses({ type, pageNumber, pageSize }) {
    return async (dispatch, getState) => {
      dispatch({ type: actions.APPEND_FORM_RESPONSES });
      const { currentAccount } = getState().account;
      if (!currentAccount) return;
      const accountId = currentAccount.id;
      const response = await Polling.handle(
        FormsService.getFormResponses(accountId, { type, pageNumber, pageSize })
      );
      if (response.error) {
        AlertHandler.error(
          retrieveErrors(response.error, message('DefaultMessages:appendFormResponsesFailure'))
        );
        dispatch(privateActionCreators.appendFormResponsesFailure(response.problem, type));
      } else {
        dispatch(privateActionCreators.appendFormResponsesSuccess(response.data, type));
      }
    };
  },
  getFormCategories(type) {
    return async (dispatch, getState) => {
      const { currentAccount } = getState().account;
      if (!currentAccount) return;
      const currentAccountId = currentAccount.id;
      dispatch({ type: actions.GET_FORM_CATEGORIES, target: `${type}FormCategories` });
      const response = await FormsService.getFormCategories(currentAccountId, type);
      if (response.ok) {
        dispatch(privateActionCreators.getFormCategoriesSuccess(response.data, type));
      } else {
        AlertHandler.error(
          retrieveErrors(response.data, message('DefaultMessages:getFormCategoriesFailure'))
        );
        dispatch(privateActionCreators.getFormCategoriesFailure(response.problem, type));
      }
    };
  },
  getPublicFormCategories(type) {
    return async dispatch => {
      dispatch({ type: actions.GET_FORM_CATEGORIES, target: `${type}FormCategories` });
      const response = await FormsService.getPublicFormCategories(type);
      if (response.ok) {
        dispatch(privateActionCreators.getFormCategoriesSuccess(response.data, type));
      } else {
        AlertHandler.error(
          retrieveErrors(response.data, message('DefaultMessages:getPublicFormCategoriesFailure'))
        );
        dispatch(privateActionCreators.getFormCategoriesFailure(response.problem, type));
      }
    };
  },
  getForm(formId, accountId, isPublic) {
    return async dispatch => {
      dispatch({ type: actions.GET_FORM, target: 'form' });
      const response = await FormsService.getForm(formId, accountId);
      if (response.ok) {
        dispatch(privateActionCreators.getFormSuccess(response.data));
      }
      if (!response.ok) {
        dispatch(push(isPublic ? ROUTES.PUBLIC_PAYMENTS : ROUTES.ACCOUNT_STATUS));
        AlertHandler.error(retrieveErrors(response.data, message('DefaultMessages:getFormFailure')));
        dispatch(privateActionCreators.getFormFailure(response.problem));
      }
    };
  },
  getFormResponseDetails(formId, accountId, formType) {
    return async dispatch => {
      dispatch({ type: actions.GET_FORM_RESPONSE_DETAILS, target: 'formDetails' });
      const response = await FormsService.getFormResponseDetails(accountId, formId);
      if (response.ok) {
        dispatch(privateActionCreators.getFormResponseDetailsSuccess(response.data, formType));
      } else {
        AlertHandler.error(
          retrieveErrors(response.data, message('DefaultMessages:getFormResponseDetailsFailure'))
        );
        dispatch(privateActionCreators.getFormResponseDetailsFailure(response.problem));
      }
    };
  },
  resetFormResponseDetails() {
    return { type: actions.RESET_FORM_RESPONSE_DETAILS, target: 'formDetails' };
  },
  clearForm() {
    return { type: actions.CLEAR_FORM };
  },
  clearFormCategories() {
    return { type: actions.CLEAR_FORM_CATEGORIES };
  },
  submitForm: (formValues, fields, formType, accountId, form, options = {}) => async (dispatch, getState) => {
    dispatch({ type: actions.SUBMIT_FORM });
    const store = getState();
    const { surveyId, complaint, avoidKeysMapping } = options;
    const newValues = await uploadFiles(
      formValues,
      fields,
      FormsService.getImageUploadUrl,
      handleUploadFailure
    );
    if (!newValues) {
      handleSubmitFormFailure(formType, form.name, privateActionCreators.submitFormFailure);
      dispatch(push(ROUTES.ACCOUNT_STATUS));
      return;
    }
    const mappedValues = avoidKeysMapping ? newValues : mapUserFormValues(fields, newValues);
    const isEmpty = objectIsEmpty(mappedValues);
    const formResponse = isEmpty ? { empty: true } : mappedValues;
    const formAccountId = accountId || (store.account.currentAccount && store.account.currentAccount.id);
    const response = await Polling.handle(
      FormsService.submitForm(formAccountId, form.id, formResponse, surveyId)
    );
    if (response.error) {
      handleSubmitFormFailure(formType, form.name, privateActionCreators.submitFormFailure, response.error);
    } else {
      const getSurveyForm = () => dispatch(SurveyActions.getSurveyForm(PROCEDURE));
      dispatch(
        alertActions.openModal({
          content: retrieveSuccessMessage(response.data, message(`DefaultMessages:${formType}SubmitSuccess`)),
          onDismiss: !surveyId && appConfig.survey[formType] && getSurveyForm
        })
      );
      if (
        (formType === CLAIM && appConfig.claims.useClaimList) ||
        (formType === PROCEDURE && appConfig.procedures.useProcedureList)
      ) {
        dispatch(actionCreators.getFormResponses({ type: formType }));
      }
      dispatch(privateActionCreators.submitFormSuccess(formType, form.name, false, complaint));
      if (!surveyId) dispatch(accountActions.getAccountAsync(formAccountId));
    }
    dispatch(push(ROUTES.ACCOUNT_STATUS));
  },
  submitPublicForm: (formValues, fields, formType, _, form, options = {}) => async dispatch => {
    dispatch({ type: actions.SUBMIT_FORM });
    const { onSuccess, onFailure, additionalValues, avoidNavigation, complaint, avoidKeysMapping } = options;
    const newValues = await uploadFiles(
      formValues,
      fields,
      FormsService.getImageUploadUrl,
      handleUploadFailure
    );
    if (!newValues) {
      handleSubmitFormFailure(formType, form.name, privateActionCreators.submitFormFailure, '', true);
      if (!avoidNavigation) dispatch(push(ROUTES.PUBLIC_DASHBOARD));
      return;
    }
    const mappedValues = avoidKeysMapping ? newValues : mapUserFormValues(fields, newValues);

    const isEmpty = objectIsEmpty(mappedValues);
    const formResponse = isEmpty
      ? { empty: true }
      : additionalValues
      ? { ...mappedValues, ...additionalValues }
      : mappedValues;
    const response = await Polling.handle(FormsService.submitPublicForm(form.id, formResponse));
    if (response.error) {
      handleSubmitFormFailure(
        formType,
        form.name,
        privateActionCreators.submitFormFailure,
        response.error,
        true
      );
      if (onFailure) onFailure();
      else if (!avoidNavigation) dispatch(push(ROUTES.PUBLIC_DASHBOARD));
    } else {
      dispatch(
        alertActions.openModal({
          content: retrieveSuccessMessage(response.data, message(`DefaultMessages:${formType}SubmitSuccess`))
        })
      );
      dispatch(privateActionCreators.submitFormSuccess(formType, form.name, true, complaint));
      if (onSuccess) onSuccess();
      else if (!avoidNavigation) dispatch(push(ROUTES.PUBLIC_DASHBOARD));
    }
  },
  createForm(formId, formType, route) {
    return async dispatch => {
      dispatch({ type: actions.CREATE_FORM, target: 'createForm', payload: { formId, formType } });
      dispatch(push(route));
    };
  },
  reiteration: (formType, id, comment, formName) => async (dispatch, getState) => {
    const { currentAccount } = getState().account;

    if (!currentAccount) return;

    const accountId = currentAccount.id;

    dispatch({ type: actions.REITERATION, target: `${formType}FormResponses` });

    const response = await Polling.handle(FormsService.reiteration(accountId, id, comment));
    if (response.error) {
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:reiterationFailure')));
      dispatch(privateActionCreators.reiterationFailure(response.error, formType));
    } else {
      dispatch(privateActionCreators.reiterationSuccess(id, formType, formName));
      AlertHandler.success(
        retrieveSuccessMessage(response.data, message('DefaultMessages:reiterationSuccess'))
      );
    }
  },

  cancelation: (formType, id, comment, formName) => async (dispatch, getState) => {
    const { currentAccount } = getState().account;
    if (!currentAccount) return;
    const accountId = currentAccount.id;
    dispatch({ type: actions.CANCELATION, target: `${formType}FormResponses` });
    const response = await Polling.handle(FormsService.cancelation(accountId, id, comment));
    if (response.error) {
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:cancelationFailure')));
      dispatch(privateActionCreators.cancelationFailure(response.error, formType));
    } else {
      dispatch(privateActionCreators.cancelationSuccess(id, formType, formName));
      AlertHandler.success(
        retrieveSuccessMessage(response.data, message('DefaultMessages:cancelationSuccess'))
      );
    }
  }
};

/* ------------- Form reducer ------------- */
const defaultState = {
  [`${CLAIM}FormResponses`]: [],
  [`${PROCEDURE}FormResponses`]: [],
  [`${CLAIM}FormCategories`]: [],
  [`${PROCEDURE}FormCategories`]: [],
  form: null,
  [`${CLAIM}FormResponsesLoading`]: false,
  [`${PROCEDURE}FormResponsesLoading`]: false,
  formLoading: false,
  error: null,
  formId: null,
  formIdError: null,
  formIdLoading: true,
  createForm: {}
};

/* eslint-disable complexity */
export function reducer(state = Immutable(defaultState), action) {
  switch (action.type) {
    case actions.GET_FORM:
    case actions.GET_FORM_CATEGORIES:
    case actions.GET_FORM_RESPONSE_DETAILS:
    case actions.GET_FORM_RESPONSES: {
      return Immutable.merge(state, { [`${action.target}Loading`]: true });
    }
    case actions.GET_FORM_SUCCESS:
    case actions.GET_FORM_CATEGORIES_SUCCESS:
    case actions.GET_FORM_RESPONSES_SUCCESS: {
      return Immutable.merge(
        state,
        {
          [`${action.target}Loading`]: false,
          [action.target]: action.payload,
          error: null
        },
        {
          deep: true
        }
      );
    }
    case actions.GET_FORM_RESPONSE_DETAILS_SUCCESS: {
      const formIndex = state[`${action.formType}FormResponses`].findIndex(
        form => form.id === action.payload.id
      );
      const updatedForm = {
        ...state[`${action.formType}FormResponses`][formIndex],
        field_details: action.payload.field_details
      };
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false,
        [`${action.formType}FormResponses`]:
          formIndex !== -1
            ? state[`${action.formType}FormResponses`]
                .slice(0, formIndex)
                .concat(updatedForm)
                .concat(state[`${action.formType}FormResponses`].slice(formIndex + 1))
            : [`${action.formType}FormResponses`]
      });
    }
    case actions.REITERATION:
    case actions.CANCELATION: {
      return Immutable.merge(state, {
        [`${action.target}Loading`]: true
      });
    }
    case actions.REITERATION_SUCCESS: {
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false,
        [`${action.target}Error`]: null
      });
    }
    case actions.CANCELATION_SUCCESS: {
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false,
        [action.target]: state[action.target].map(formItem =>
          formItem.id === action.payload
            ? {
                ...formItem,
                cancellable: false,
                reiterable: false,
                status: message('FieldDetails:cancelled'),
                status_label: message('FieldDetails:cancelled2')
              }
            : formItem
        ),
        [`${action.target}Error`]: null
      });
    }
    case actions.REITERATION_FAILURE:
    case actions.CANCELATION_FAILURE:
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false,
        error: { what: action.payload.error, where: action.type }
      });
    case actions.APPEND_FORM_RESPONSES_FAILURE: {
      return Immutable.merge(state, {
        appendFormResponsesLoading: false,
        error: { what: action.payload.error, where: action.type }
      });
    }

    case actions.GET_FORM_CATEGORIES_FAILURE:
    case actions.GET_FORM_RESPONSE_DETAILS_FAILURE:
    case actions.GET_FORM_RESPONSES_FAILURE: {
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false,
        error: { what: action.payload.error, where: action.type }
      });
    }
    case actions.GET_FORM_FAILURE:
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false,
        error: { what: action.payload.error, where: action.type }
      });
    case actions.CLEAR_FORM: {
      return Immutable.merge(state, { form: null });
    }
    case actions.CLEAR_FORM_CATEGORIES: {
      return Immutable.merge(state, { formCategories: [] });
    }
    case actions.APPEND_FORM_RESPONSES_SUCCESS: {
      return Immutable.merge(state, {
        [action.target]: [...state[action.target], ...action.payload],
        error: null,
        appendFormResponsesLoading: false
      });
    }
    case actions.APPEND_FORM_RESPONSES: {
      return Immutable.merge(state, {
        appendFormResponsesLoading: true
      });
    }
    case actions.CREATE_FORM: {
      return Immutable.merge(state, {
        [action.target]: action.payload,
        form: state.form && state.form.id === action.payload.formId && state.form
      });
    }
    case actions.RESET_FORM_RESPONSE_DETAILS: {
      return Immutable.merge(state, {
        [action.target]: null
      });
    }
    case actions.SUBMIT_FORM:
      return Immutable.merge(state, {
        submitFormLoading: true
      });
    case actions.SUBMIT_FORM_FAILURE:
    case actions.SUBMIT_FORM_SUCCESS:
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false
      });
    default: {
      return state;
    }
  }
}
/* eslint-enable complexity */

/* ------------- Form propTypes ------------- */

export const formTypePropType = PropTypes.oneOf([CLAIM, PROCEDURE]);

export const statusPropType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    description: PropTypes.string,
    isPending: PropTypes.bool
  })
]);

export const formResponsePropTypes = PropTypes.shape({
  id: PropTypes.number.isRequired,
  form_id: PropTypes.number.isRequired,
  form_name: PropTypes.string.isRequired,
  account_id: PropTypes.number.isRequired,
  external_id: PropTypes.number,
  status: statusPropType,
  action_type: PropTypes.string.isRequired,
  reiteration_count: PropTypes.number.isRequired,
  started_at: PropTypes.string,
  expected_completion_at: PropTypes.string,
  completion_at: PropTypes.string,
  created_by: PropTypes.string.isRequired,
  last_operation_by: PropTypes.string.isRequired,
  cancellable: PropTypes.bool.isRequired,
  reiterable: PropTypes.bool.isRequired,
  form_type: formTypePropType,
  field_details: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string.isRequired, value: PropTypes.string.isRequired })
  ).isRequired
});

export const formPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  form_category_id: PropTypes.number.isRequired,
  url: PropTypes.string,
  name: PropTypes.string.isRequired,
  cancellable: PropTypes.bool.isRequired,
  reiterable: PropTypes.bool.isRequired,
  auto_closure: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(['Forms::Procedure', 'Forms::Claim', 'Forms::Complaint']).isRequired
});

export const formCategoryPropTypes = PropTypes.shape({
  id: PropTypes.number.isRequired,
  utility_id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  forms_type: formTypePropType.isRequired,
  forms: PropTypes.arrayOf(formPropType).isRequired
});
